/**
 * Основная кнопка оплаты — единая точка выбора платёжного провайдера.
 *
 * Касса рублей — lib/rubProvider.js (решает бэкенд, backend/rub_provider.py):
 *   yookassa  (астрофил.рф; astrofeel.ru после включения магазина 1335266)
 *                                         → POST /api/yookassa/create-payment
 *   robokassa (astrofeel.ru до включения) → POST /api/robokassa/create
 *                                           POST /api/robokassa/create-horary-pack
 *
 * Возвращает адрес, куда отправить браузер, и кладёт в localStorage ключ,
 * по которому /payment/success и /account сверяют платёж после возврата
 * (yookassa_payment_id — ЮKassa, robokassa_invoice_id — Robokassa; оба
 * ключа уже читаются PaymentSuccessPage.jsx).
 *
 * Отличия полей (сверено с бэкендом):
 *   ЮKassa:    { plan, auto_renew, consent_to_auto_renewal, ym_client_id? }
 *              ответ { confirmation_url, payment_id }
 *   Robokassa: { plan, amount: 0, consent_to_auto_renewal } — при amount ≤ 0
 *              бэкенд берёт цену из ROBOKASSA_PLAN_PRICES; ym_client_id у
 *              роута нет. Согласие на автосписание учитывается только при
 *              включённых ROBOKASSA_CONSENT_UI_ENABLED и
 *              ROBOKASSA_RECURRING_ENABLED, иначе счёт всегда разовый.
 *              ответ { payment_url, invoice_id }
 *   Пакет хорара Robokassa: { pack_type: "3" | "10" } — ответ тот же.
 *
 * Во все кассы уходит источник покупки (07.10.2026): attribution — UTM
 * текущего визита и referrer (lib/attribution.js getVisitAttribution) — и
 * ym_client_id — номер посетителя Метрики. Сервер кладёт их в запись платежа.
 */
import { BACKEND_URL as API_URL } from '@/lib/backendUrl';
import { PAYMENT_PROVIDER, IS_ROBOKASSA } from '@/lib/siteConfig';
import { ensureRubProvider, ROBOKASSA } from '@/lib/rubProvider';
import { STRIPE_PRIMARY, VISITOR_TIMEZONE } from '@/lib/visitorRegion';
import { getVisitAttribution } from '@/lib/attribution';
import { ymClientIdAsync } from '@/lib/analytics';
import { singleCheckout } from '@/lib/checkoutGuard';

export { PAYMENT_PROVIDER, IS_ROBOKASSA };

/**
 * Источник покупки для тела платежа: { attribution?, ym_client_id? }.
 * ClientID Метрики ждём не дольше 300 мс — оплата аналитику не ждёт.
 */
export async function paymentSourceFields(ymClientId = null) {
  const out = {};
  const attribution = getVisitAttribution();
  if (attribution && Object.keys(attribution).length) out.attribution = attribution;
  let ym = ymClientId;
  if (!ym) {
    try { ym = await ymClientIdAsync(300); } catch { ym = null; }
  }
  if (ym) out.ym_client_id = String(ym);
  return out;
}

function authHeaders() {
  const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

/**
 * @param {object} opts
 * @param {string} opts.plan            код плана (ASTROFEEL_MONTHLY, PRO_YEARLY, FOUNDER, HORARY_PACK_3 …)
 * @param {boolean} [opts.autoRenew]    согласие на автопродление (обе кассы)
 * @param {string|null} [opts.ymClientId] ClientID Метрики (только ЮKassa)
 * @param {"3"|"10"|null} [opts.horaryPackType] размер пакета хорара (Robokassa зовёт отдельный роут)
 * @param {object|null} [opts.extra]    дополнительные поля платежа ЮKassa (para_pair_id — покупка пары)
 * @returns {Promise<{ok:boolean, redirectUrl?:string, status?:number, error?:string, data:object}>}
 */
export function createPrimaryPayment(opts) {
  return singleCheckout(() => createPrimaryPaymentNow(opts), goesToPayment);
}

/** Уходит ли вызывающий в кассу по ответу create*Payment. */
export function goesToPayment(result) {
  return !!(result && result.ok && result.redirectUrl);
}

/**
 * То же без защиты от повторного нажатия (lib/checkoutGuard.js) — для
 * вызовов изнутри уже защищённой покупки (buyCalcUnlock).
 */
export async function createPrimaryPaymentNow({ plan, autoRenew = false, ymClientId = null, horaryPackType = null, extra = null }) {
  let url;
  let body;
  const source = await paymentSourceFields(ymClientId);
  // Касса рублей — по ответу бэкенда (на ру: Робокасса или ЮKassa 1335266).
  const viaRobokassa = (await ensureRubProvider()) === ROBOKASSA;

  if (viaRobokassa) {
    if (horaryPackType) {
      url = `${API_URL}/api/robokassa/create-horary-pack`;
      body = { pack_type: String(horaryPackType) };
    } else {
      url = `${API_URL}/api/robokassa/create`;
      // consent_to_auto_renewal — галочка согласия на автосписание (ру).
      // Бэкенд считает согласием ТОЛЬКО явное true: галочка не отмечена по
      // умолчанию, поэтому ни отсутствие поля, ни false рекуррентным счёт
      // не делают, и платёж проходит разовым.
      body = { plan, amount: 0, consent_to_auto_renewal: !!autoRenew };
    }
  } else {
    url = `${API_URL}/api/yookassa/create-payment`;
    body = { plan, auto_renew: !!autoRenew, consent_to_auto_renewal: !!autoRenew };
    if (extra) body = { ...extra, ...body };
  }
  body = { ...body, ...source };

  const res = await fetch(url, { method: 'POST', headers: authHeaders(), body: JSON.stringify(body) });

  let data = {};
  try {
    data = await res.json();
  } catch {
    // Non-JSON body (e.g. an HTML 502 page from a proxy) — keep data = {}.
  }

  const redirectUrl = data.confirmation_url || data.payment_url;
  if (res.ok && redirectUrl) {
    try {
      // Ключ другой кассы убираем: страница успеха сверяет платёж по тому,
      // что лежит в localStorage, и старый номер не должен её путать.
      if (viaRobokassa) {
        if (data.invoice_id != null) localStorage.setItem('robokassa_invoice_id', String(data.invoice_id));
        localStorage.removeItem('yookassa_payment_id');
        localStorage.removeItem('yookassa_payment_plan');
      } else if (data.payment_id) {
        localStorage.setItem('yookassa_payment_id', String(data.payment_id));
        localStorage.setItem('yookassa_payment_plan', String(plan));
        localStorage.removeItem('robokassa_invoice_id');
      }
    } catch {
      /* localStorage недоступен — оплата всё равно продолжается */
    }
    return { ok: true, redirectUrl, data };
  }

  return {
    ok: false,
    status: res.status,
    error: data.detail || data.message || `Ошибка ${res.status}`,
    data,
  };
}

/**
 * Разовая покупка программы (199 ₽ и её иностранная цена).
 *
 * Иностранцу на ру (STRIPE_PRIMARY, lib/visitorRegion.js) — Stripe Checkout,
 * POST /api/stripe/checkout-one-time: сумму и валюту (1 190 ₸ / 2,49 $)
 * считает сервер по поясу браузера. Всем остальным — основная касса, как
 * раньше (createPrimaryPayment). На рф STRIPE_PRIMARY всегда false.
 *
 * extra — calc_unlock_id или para_pair_id, как у ЮKassa.
 */
export function createOneTimePayment(opts) {
  return singleCheckout(() => createOneTimePaymentNow(opts), goesToPayment);
}

/** createOneTimePayment без защиты от повторного нажатия (buyCalcUnlock). */
export async function createOneTimePaymentNow({
  plan, ymClientId = null, extra = null, stripePrimary = STRIPE_PRIMARY, returnPath = null,
}) {
  if (!stripePrimary) return createPrimaryPaymentNow({ plan, ymClientId, extra });
  const body = {
    ...(extra || {}),
    plan,
    origin_url: window.location.origin,
    timezone: VISITOR_TIMEZONE || null,
    // Отказ от оплаты на странице Stripe возвращает сюда — в программу.
    // returnPath — когда платят не со страницы программы (экран неудачной
    // оплаты): иначе отказ вернул бы на этот экран.
    return_path: returnPath || `${window.location.pathname}${window.location.search}`,
    ...(await paymentSourceFields(ymClientId)),
  };
  let res;
  try {
    res = await fetch(`${API_URL}/api/stripe/checkout-one-time`, {
      method: 'POST', headers: authHeaders(), body: JSON.stringify(body),
    });
  } catch (_e) {
    return { ok: false, status: 0, error: 'Нет связи с сервером', data: {} };
  }
  let data = {};
  try { data = await res.json(); } catch { /* не JSON */ }
  if (res.ok && data.url) return { ok: true, redirectUrl: data.url, data };
  return { ok: false, status: res.status, error: data.detail || `Ошибка ${res.status}`, data };
}

/**
 * ПРО иностранной картой на рф (ссылка «Оплатить иностранной картой»,
 * lib/foreignCard.js): Stripe Checkout тем же расчётом, что на ру
 * (/api/stripe/checkout-multicurrency), в валюте посетителя — тенге для
 * Казахстана, доллары остальным. Льготный первый месяц (499) Stripe не
 * продаёт — уходит обычный месяц.
 */
export async function createForeignProPayment(plan) {
  const { stripeProPlan } = await import('@/lib/foreignCard');
  const { stripeDefaultCurrency } = await import('@/lib/visitorRegion');
  const { createMultiCurrencyCheckout } = await import('@/hooks/useCurrency');
  const stripePlan = stripeProPlan(plan);
  if (!stripePlan) return { ok: false, error: 'Этот тариф иностранной картой не оплачивается' };
  try {
    const data = await createMultiCurrencyCheckout(stripePlan, window.location.origin, stripeDefaultCurrency());
    if (data && data.url) return { ok: true, redirectUrl: data.url, data };
    return { ok: false, error: 'Ошибка создания платежа', data };
  } catch (err) {
    return { ok: false, error: (err && err.message) || 'Ошибка создания платежа' };
  }
}

/** Пакеты вопросов хорара, которые Stripe продаёт (stripe_one_time.HORARY_PACKS). */
export const HORARY_PACK_PLANS = ['HORARY_PACK_3', 'HORARY_PACK_10'];

/**
 * Оплатить иностранной картой то, что человек пытался купить через ЮKassa
 * (экран «Оплата не прошла?», 28.10.2026). План — из localStorage
 * (yookassa_payment_plan), id расчёта или пары — из ключей возврата,
 * которые страница программы положила перед уходом в кассу.
 *
 * Возвращает то же, что createOneTimePayment / createForeignProPayment;
 * { ok:false, unsupported:true } — план Stripe не продаёт (ЛАЙТ и т. п.),
 * человека стоит отправить на /pricing.
 */
export async function createForeignPayment(plan) {
  const code = String(plan || '').toUpperCase();
  const { stripeProPlan } = await import('@/lib/foreignCard');
  if (stripeProPlan(code)) return createForeignProPayment(code);

  const read = (key) => {
    try { return localStorage.getItem(key); } catch (_e) { return null; }
  };
  const {
    CALC_UNLOCK_APPS, CALC_UNLOCK_RETURN_KEY, CALC_UNLOCK_RETURN_PATH_KEY,
  } = await import('@/lib/calcUnlock');
  const calc = Object.values(CALC_UNLOCK_APPS).find((cfg) => cfg.plan === code);
  if (calc) {
    const calcId = read(CALC_UNLOCK_RETURN_KEY);
    if (!calcId) return { ok: false, unsupported: true, error: 'Расчёт не найден — откройте программу и нажмите «Открыть полностью» ещё раз.' };
    return createOneTimePayment({
      plan: code, extra: { calc_unlock_id: calcId }, stripePrimary: true,
      returnPath: read(CALC_UNLOCK_RETURN_PATH_KEY) || null,
    });
  }
  if (code === 'PARA_PAIR_ONE_TIME') {
    const pairId = read('para_return_pair');
    if (!pairId) return { ok: false, unsupported: true, error: 'Пара не найдена — посчитайте совместимость ещё раз.' };
    return createOneTimePayment({ plan: code, extra: { para_pair_id: pairId }, stripePrimary: true, returnPath: '/para' });
  }
  if (code === 'CHAKRY_ONE_TIME') {
    return createOneTimePayment({ plan: code, stripePrimary: true, returnPath: '/chakry-po-natalnoy-karte?calc=1' });
  }
  if (HORARY_PACK_PLANS.includes(code)) {
    return createOneTimePayment({ plan: code, stripePrimary: true, returnPath: '/horar' });
  }
  return { ok: false, unsupported: true, error: 'Этот тариф иностранной картой не оплачивается' };
}
